import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import useKeyPress from '@ally/use-key-press';
import { TextBody } from '../../../../Typography';
import { visual } from '../../../strings';
export const baseAutoCompleteA11yProps = {
    role: 'combobox',
    'aria-autocomplete': 'both',
    'aria-expanded': 'false',
};
const OptionsList = styled.ul `
  ${({ theme: { colors } }) => `
    background: ${colors.white};
    padding: 0;
    margin: 0;
  `}
`;
const OptionsItem = styled.li `
  ${({ theme: { colors, space } }) => `
    background: ${colors.white};
    padding: ${space.xs} ${space.md};
    list-style: none;

    &:hover, &:focus {
      cursor: pointer;
      background: ${colors['page-gray']};
      outline: none;
    }
  `}
`;
// no need for uuid
// only one auto-complete can be opened at a time
const domId = (index) => `mui-auto-complete-${index}`;
const AutoCompleteSelect = ({ options, onSelect, onA11yPropsUpdate, noResultsText = visual.noResults, }) => {
    const { current: listId } = useRef(`mui-auto-complete-list-${uuid()}`);
    const [focusIndex, setFocusIndex] = useState(null);
    useEffect(() => {
        onA11yPropsUpdate({
            role: 'combobox',
            'aria-expanded': 'true',
            'aria-autocomplete': 'both',
            'aria-owns': listId,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const updateFocus = useCallback((nextIndex) => {
        var _a;
        (_a = document.getElementById(domId(nextIndex))) === null || _a === void 0 ? void 0 : _a.focus();
        setFocusIndex(nextIndex);
        onA11yPropsUpdate({
            role: 'combobox',
            'aria-expanded': 'true',
            'aria-autocomplete': 'both',
            'aria-activedescendant': domId(nextIndex),
            'aria-owns': listId,
        });
    }, [setFocusIndex, onA11yPropsUpdate, listId]);
    useKeyPress(['ArrowUp', 'ArrowLeft'], () => {
        const prevIndex = focusIndex !== null && focusIndex !== void 0 ? focusIndex : 1;
        const nextIndex = prevIndex === 0 ? options.length - 1 : prevIndex - 1;
        updateFocus(nextIndex);
    });
    useKeyPress(['ArrowDown', 'ArrowRight'], () => {
        const prevIndex = focusIndex !== null && focusIndex !== void 0 ? focusIndex : -1;
        const nextIndex = prevIndex === options.length - 1 ? 0 : prevIndex + 1;
        updateFocus(nextIndex);
    });
    return (React.createElement(OptionsList, { id: listId, "data-testid": "mui-auto-complete-list", role: "group" },
        !options.length && (React.createElement(OptionsItem, { "data-testid": "mui-auto-complete-no-results", role: "menuitem" },
            React.createElement(TextBody, { tag: "span", size: "md" }, noResultsText))),
        options.map((option, index) => (React.createElement(OptionsItem, { id: domId(index), key: option.id, role: "menuitem", tabIndex: -1, "aria-label": option.label, onClick: () => {
                onSelect(option);
            }, onKeyPress: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    onSelect(option);
                }
            } },
            React.createElement(TextBody, { tag: "span", size: "md" }, option.label))))));
};
export { AutoCompleteSelect };
//# sourceMappingURL=AutoCompleteSelect.js.map